import { CSSProperties, FC, ReactNode } from 'react';

import { NavigationItem } from '@telialt/models/src/Navigation';

import { NAVIGATION_CONTEXT_BAR_HEIGHT, NAVIGATION_MAIN_BAR_HEIGHT } from '../constants';
import { useStickyNavigation } from '../hooks/useStickyNavigation';
import { MaintenanceResponseData } from '../model';
import { NAVIGATION_TEST_IDS } from '../test-ids';
import { BurgerMenu } from './BurgerMenu/BurgerMenu';
import { LogoItem } from './LogoItem/LogoItem';
import { Maintenance } from './Maintenance/Maintenance';
import { MenuItemList } from './MenuItemList/MenuItemList';
import {
    ContextBar,
    CustomContentContainer,
    LogoBurgerContainer,
    LogoMenuContainer,
    MainBar,
    NavigationContentContainer,
    StyledNavigation,
    TopBar,
} from './Navigation.styles';
import { NavigationBannerSlot } from './NavigationBannerSlot/NavigationBannerSlot';
import { NavigationSkeleton } from './NavigationSkeleton/NavigationSkeleton';
import { SkipToContent } from './SkipToContent/SkipToContent';

type Props = {
    isLoading?: boolean;
    contextLeftMenu?: NavigationItem[];
    contextRightMenu?: NavigationItem[];
    mainLeftMenu?: NavigationItem[];
    mainRightMenu?: NavigationItem[];
    additionalMenu?: NavigationItem[];
    maintenance?: MaintenanceResponseData;
    customContent?: ReactNode;
};

export const Navigation: FC<Props> = ({
    contextLeftMenu,
    contextRightMenu,
    mainLeftMenu,
    mainRightMenu,
    additionalMenu,
    isLoading,
    maintenance,
    customContent,
}) => {
    const hasContextBar = contextLeftMenu?.length > 0 || contextRightMenu?.length > 0;

    const { setNavigationBannerRef, mainBarOffset, topBarOffset, height, heightMobile, position, duration } =
        useStickyNavigation(isLoading || hasContextBar);

    if (isLoading) {
        return <NavigationSkeleton />;
    }

    const burgerMenu = [...(additionalMenu || []), ...(mainLeftMenu || [])];

    return (
        <StyledNavigation
            data-test={NAVIGATION_TEST_IDS.ROOT}
            data-section="Header"
            $height={height}
            $heightMobile={heightMobile}
            style={
                {
                    '--navigation-main-bar-offset': `${mainBarOffset}px`,
                    '--navigation-top-bar-offset': `${topBarOffset}px`,
                    '--navigation-position': position,
                    '--navigation-offset-duration': `${duration}ms`,
                } as CSSProperties
            }
        >
            <TopBar ref={setNavigationBannerRef}>
                <NavigationBannerSlot />
                <Maintenance data={maintenance} />
                <SkipToContent />
                {hasContextBar && (
                    <ContextBar $height={NAVIGATION_CONTEXT_BAR_HEIGHT} data-test={NAVIGATION_TEST_IDS.CONTEXT_BAR}>
                        <NavigationContentContainer>
                            <MenuItemList items={contextLeftMenu} data-section="Other" />
                            <MenuItemList items={contextRightMenu} data-section="Other" />
                        </NavigationContentContainer>
                    </ContextBar>
                )}
            </TopBar>

            <MainBar $height={NAVIGATION_MAIN_BAR_HEIGHT}>
                <NavigationContentContainer>
                    <LogoMenuContainer>
                        <LogoBurgerContainer>
                            <BurgerMenu items={burgerMenu} contextItems={contextLeftMenu} />
                            <LogoItem />
                        </LogoBurgerContainer>
                        <MenuItemList items={mainLeftMenu} displayOption="Desktop only" data-section="Menu" />
                    </LogoMenuContainer>
                    <MenuItemList items={mainRightMenu} data-section="Other" />
                    {!!customContent && <CustomContentContainer>{customContent}</CustomContentContainer>}
                </NavigationContentContainer>
            </MainBar>
        </StyledNavigation>
    );
};
